/**
 * CapacityEmployeeRow.tsx
 * One employee's row(s) in the "Team Allocation & Weekly Capacity" grid
 * (staffing-allocation tile restyle 2026-07-13). Extracted from
 * Phase2CapacityTable to keep both files under the 600-line cap.
 *
 *   Tile row — per week: a matrix-style TILE (CapacityWeekCell) = the
 *     utilization band + % on top, an inline allocation-% stepper below, and the
 *     day-off / conflict signal ON the tile. NO per-day "N tests" count cells —
 *     Gigi found those confusing (2026-07-13). Clicking a tile expands the
 *     day-by-day WO/task breakdown for that (person, week) below.
 *   Expand row — the inline CapacityWeekExpand for the currently-expanded week
 *     (only rendered when a tile on this row is open).
 *
 * The Employee-name gutter carries a remove (×) that drops the person from the
 * plan (all weeks of the source). Colors via theme tokens — no hex literals.
 */

import React from 'react';
import { IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, useTheme } from '@mui/material/styles';

import { CapacityWeekCell } from './CapacityWeekCell';
import { CapacityWeekExpand } from './CapacityWeekExpand';
import { EMP_COL_W, ROLE_COL_W, WEEK_W, type EmployeeRow } from './phase2CapacityShared';
import { busyDayFractionLabel } from '../../../../utils/personDayDisplay';
import {
  type EmployeeWeekCell,
  type CrossScenarioSource,
  type AssignmentBarDetail,
  type SourceAllocationDetail,
} from '../../../../services/scheduleEmployeeSchedule';

/** Lazily-fetched detail for the currently-expanded (person, week). */
export interface WeekExpandDetail {
  assignments: AssignmentBarDetail[];
  sourceAllocations: SourceAllocationDetail[];
  isLoading: boolean;
  error: string | null;
}

export interface CapacityEmployeeRowProps {
  emp: EmployeeRow;
  rowBg?: string;
  weekColumns: string[];
  holidaySet: Set<string>;
  /** Look up the capacity cell for a week (band + hard signals). */
  cellFor: (weekStart: string) => EmployeeWeekCell | undefined;
  /** This plan's source allocation % for a week (0 when not on the roster that week). */
  sourcePercentFor: (weekStart: string) => number;
  /** OTHER draft plans holding this person (advisory soft note in the expand). */
  crossSources: CrossScenarioSource[];
  hoveredWeek?: string | null;
  onHoverWeek?: (weekStart: string | null) => void;
  /** Plan locked → hide the stepper + remove, keep the read-only tile + expand. */
  editable: boolean;
  /** The week of THIS row currently expanded (null = none). */
  expandedWeek: string | null;
  /** Detail for the expanded week (undefined until the fetch is armed). */
  expandDetail?: WeekExpandDetail;
  onSetPercent: (weekStart: string, percent: number) => void;
  onRemove: () => void;
  /** Toggle the inline day breakdown for (this person, weekStart). */
  onToggleExpand: (weekStart: string) => void;
}

/** True when the week has any day marked as a dated absence. */
function weekHasDayOff(cell: EmployeeWeekCell | undefined): boolean {
  if (!cell?.byDayAbsent) return false;
  return Object.values(cell.byDayAbsent).some(Boolean);
}

export const CapacityEmployeeRow: React.FC<CapacityEmployeeRowProps> = ({
  emp,
  rowBg,
  weekColumns,
  holidaySet,
  cellFor,
  sourcePercentFor,
  crossSources,
  hoveredWeek,
  onHoverWeek,
  editable,
  expandedWeek,
  expandDetail,
  onSetPercent,
  onRemove,
  onToggleExpand,
}) => {
  const theme = useTheme();
  const beamRail = `2px solid ${theme.schedule.selection}`;
  const gutterBg = rowBg ?? theme.palette.background.paper;
  const fullColSpan = 2 + weekColumns.length * 7;

  return (
    <React.Fragment>
      {/* ── Tile row — utilization band + % + inline stepper per week. ── */}
      <TableRow data-flash-user={emp.userKey} sx={{ backgroundColor: rowBg }}>
        {/* Employee name (sticky) — carries the remove ×. */}
        <TableCell
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 1,
            backgroundColor: gutterBg,
            borderRight: `1px solid ${theme.border.color}`,
            py: 0.75,
            px: 1.5,
            width: EMP_COL_W,
            verticalAlign: 'middle',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Typography variant="body2" fontWeight={500} noWrap sx={{ fontSize: '0.82rem', flex: 1, minWidth: 0 }}>
              {emp.displayName}
            </Typography>
            {editable && (
              <Tooltip title={`Remove ${emp.displayName} from this plan`} arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={onRemove}
                    aria-label={`Remove ${emp.displayName}`}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </TableCell>

        {/* Role — sticky (frozen gutter), with the frozen-column right divider. */}
        <TableCell
          sx={{
            position: 'sticky',
            left: EMP_COL_W,
            zIndex: 1,
            backgroundColor: gutterBg,
            boxShadow: `inset -1px 0 0 0 ${theme.border.color}, 6px 0 6px -6px ${alpha(theme.palette.text.primary, 0.18)}`,
            py: 0.75,
            px: 1.5,
            width: ROLE_COL_W,
            verticalAlign: 'middle',
          }}
        >
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem' }}>
            {emp.role ?? '—'}
          </Typography>
        </TableCell>

        {/* One tile per week (spans the 7 day columns). */}
        {weekColumns.map((ws) => {
          const cell = cellFor(ws);
          const hot = hoveredWeek === ws;
          return (
            <TableCell
              key={ws}
              colSpan={7}
              align="center"
              onMouseEnter={() => onHoverWeek?.(ws)}
              sx={{
                width: WEEK_W,
                px: 0,
                py: 0.25,
                backgroundColor: hot ? alpha(theme.schedule.selection, 0.06) : undefined,
                borderBottom: `1px solid ${theme.border.color}`,
                borderRight: hot ? beamRail : `2px solid ${theme.border.color}`,
                ...(hot ? { borderLeft: beamRail } : {}),
              }}
            >
              <CapacityWeekCell
                allocationPercent={cell?.allocationPercent}
                subLabel={busyDayFractionLabel(cell)}
                sourcePercent={sourcePercentFor(ws)}
                editable={editable}
                hasConflict={cell?.hasOverbook ?? false}
                hasDayOff={weekHasDayOff(cell)}
                expanded={expandedWeek === ws}
                onToggleExpand={() => onToggleExpand(ws)}
                onSetPercent={(p) => onSetPercent(ws, p)}
              />
            </TableCell>
          );
        })}
      </TableRow>

      {/* ── Expand row — inline day-by-day WO/task breakdown for the open week. ── */}
      {expandedWeek && (
        <TableRow sx={{ backgroundColor: rowBg }}>
          <TableCell
            colSpan={fullColSpan}
            sx={{ p: 0, borderBottom: `1px solid ${theme.border.color}`, backgroundColor: theme.schedule.selectionBg }}
          >
            <CapacityWeekExpand
              weekStart={expandedWeek}
              assignments={expandDetail?.assignments ?? []}
              sourceAllocations={expandDetail?.sourceAllocations ?? []}
              crossSources={crossSources}
              isLoading={expandDetail?.isLoading ?? true}
              error={expandDetail?.error ?? null}
              holidaySet={holidaySet}
            />
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};
